import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
  calculateJournalTotals,
  validateJournalLines,
} from "../utils/accountsHelpers";

const emptyLine = {
  accountId: "",
  accountCode: "",
  accountName: "",
  debit: 0,
  credit: 0,
  narration: "",
};

const JournalForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    journalType: "general",
    transactionDate: "",
    postingDate: "",
    narration: "",
    referenceNo: "",
    lines: [emptyLine, { ...emptyLine }],
  });

  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (initialData) {
      setFormData({
        journalType: initialData.journalType || "general",
        transactionDate: initialData.transactionDate || "",
        postingDate: initialData.postingDate || "",
        narration: initialData.narration || "",
        referenceNo: initialData.referenceNo || "",
        lines: initialData.lines?.length
          ? initialData.lines
          : [emptyLine, { ...emptyLine }],
      });
    }
  }, [initialData]);

  const handleLineChange = (index, key, value) => {
    const next = [...formData.lines];
    next[index] = { ...next[index], [key]: value };
    setFormData((prev) => ({ ...prev, lines: next }));
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { ...emptyLine }],
    }));
  };

  const removeLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index),
    }));
  };

  const totals = calculateJournalTotals(formData.lines);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateJournalLines(formData.lines);
    if (!validation.valid) {
      setValidationError(validation.message);
      return;
    }
    setValidationError("");
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Journal Type"
            value={formData.journalType}
            onChange={(e) =>
              setFormData((p) => ({ ...p, journalType: e.target.value }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Transaction Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.transactionDate}
            onChange={(e) =>
              setFormData((p) => ({ ...p, transactionDate: e.target.value }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Posting Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.postingDate}
            onChange={(e) =>
              setFormData((p) => ({ ...p, postingDate: e.target.value }))
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Narration"
            value={formData.narration}
            onChange={(e) =>
              setFormData((p) => ({ ...p, narration: e.target.value }))
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600}>
            Journal Lines
          </Typography>
        </Grid>

        {formData.lines.map((line, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Account ID"
                value={line.accountId}
                onChange={(e) =>
                  handleLineChange(index, "accountId", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Account Code"
                value={line.accountCode}
                onChange={(e) =>
                  handleLineChange(index, "accountCode", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Account Name"
                value={line.accountName}
                onChange={(e) =>
                  handleLineChange(index, "accountName", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Debit"
                type="number"
                value={line.debit}
                onChange={(e) =>
                  handleLineChange(index, "debit", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={5} md={2}>
              <TextField
                label="Credit"
                type="number"
                value={line.credit}
                onChange={(e) =>
                  handleLineChange(index, "credit", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>
            <Grid
              item
              xs={1}
              md={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <IconButton color="error" onClick={() => removeLine(index)}>
                <Delete />
              </IconButton>
            </Grid>
          </React.Fragment>
        ))}

        <Grid item xs={12}>
          <Button startIcon={<Add />} onClick={addLine}>
            Add Line
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography>Total Debit: {totals.totalDebit}</Typography>
          <Typography>Total Credit: {totals.totalCredit}</Typography>
          <Typography color={totals.isBalanced ? "success.main" : "error.main"}>
            {totals.isBalanced ? "Balanced" : "Not Balanced"}
          </Typography>
          {!!validationError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {validationError}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button type="submit" variant="contained" disabled={loading}>
          {initialData ? "Update Journal" : "Create Journal"}
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </form>
  );
};

export default JournalForm;
